import { applePayCredentialsAtom, venueAtom, userInfoAtom, paymentMethodsAtom, applePayUserAtom } from '@/state/global'
import { PaymentDataAtom } from '@/state/payment'
import { getApplePayMerchantSessionAPI, getApplePaySessionAPI } from '@/utils/apis'
import { PaymentMethodInputTypes } from '@/utils/constants'
import { formatPaymentUserName } from '@/utils/helpers'
import { ApplePayCredentials } from '@/utils/types'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useState } from 'react'

type UseApplePayReturn = {
  handleApplePayClick: (totalAmount?: string) => Promise<unknown>
  isApplePaySupported: boolean
}

export function useApplePay(): UseApplePayReturn {
  const [applePayCredentials, setApplePayCrendentials] = useAtom(applePayCredentialsAtom)
  const venue = useAtomValue(venueAtom)
  const storedUser = useAtomValue(userInfoAtom)
  const setApplePayUser = useSetAtom(applePayUserAtom)
  const [paymentMethods, setPaymentMethods] = useAtom(paymentMethodsAtom)
  const [paymentMethod, setPaymentMethod] = useAtom(PaymentDataAtom)
  const [merchantSession, setMerchantSession] = useState<{ merchantSession: string } | null>(null)

  async function fetchMerchantSession(validationURL: string) {
    try {
      // if for some reason we don't have the apple pay merchant ID (very rare), we can call backend for it again
      let applePayCredentialsRetry: ApplePayCredentials | null | undefined
      if (!applePayCredentials?.merchantId) {
        applePayCredentialsRetry = await getApplePaySessionAPI(venue.venue_uuid, venue.payment_processor || '')

        if (applePayCredentialsRetry?.merchantId) {
          setApplePayCrendentials(applePayCredentialsRetry)
        }
      }
      const applePayMerchantSession = await getApplePayMerchantSessionAPI(
        'fortis',
        applePayCredentials?.merchantId || applePayCredentialsRetry?.merchantId || '',
        venue?.name || 'Union',
        validationURL
      )
      setMerchantSession(applePayMerchantSession)
      return applePayMerchantSession?.merchantSession
    } catch (error) {
      console.error('Failed to fetch Apple Pay merchant session:', error)
    }
  }

  // Check if ApplePaySession is supported — only for Fortis (Magensa uses useMagensaApplePay via TEConnect)
  const isApplePaySupported =
    typeof window !== 'undefined' &&
    window?.ApplePaySession &&
    window?.ApplePaySession.canMakePaymentsWithActiveCard(applePayCredentials?.merchantId) &&
    venue?.apple_pay_enabled &&
    venue?.payment_processor !== 'magensa_unigate'

  const handleApplePayClick = (totalAmount?: string) => {
    return new Promise((resolve, reject) => {
      // Define the Apple Pay version and payment request details
      const version = 4 // Use the appropriate version number
      const paymentRequest = {
        countryCode: 'US',
        currencyCode: 'USD',
        merchantCapabilities: ['supports3DS'],
        supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
        total: {
          label: venue?.name,
          type: 'final',
          amount: `${totalAmount ? totalAmount : '0.00'}`, // The actual total amount should be provided here
        },
        requiredShippingContactFields: storedUser ? [] : ['name', 'phone', 'email'],
      }

      try {
        const session = new window.ApplePaySession(version, paymentRequest)

        session.onvalidatemerchant = async (event: { validationURL: string }) => {
          try {
            const applePayMerchantSession = await fetchMerchantSession(event.validationURL)
            session.completeMerchantValidation(applePayMerchantSession)
          } catch (error) {
            console.error('Failed to validate merchant:', error)
            reject(error) // Reject the promise if merchant validation fails
          }
        }

        session.onpaymentmethodselected = (e: any) => {
          // Define ApplePayPaymentMethodUpdate based on the selected payment method.
          // No updates or errors are needed, pass an empty object.
          const newTotal = {
            label: venue?.name,
            ...(totalAmount ? { type: 'final', amount: totalAmount } : { type: 'pending', amount: '0.00' }),
          }

          const update = {
            newTotal: newTotal,
          }
          session.completePaymentMethodSelection(update)
        }

        session.onpaymentauthorized = (event: any) => {
          const paymentToken = event.payment.token
          const shippingContact = event.payment?.shippingContact

          const result = {
            status: session.STATUS_SUCCESS,
          }
          // display name means display name of card, not user --> "Visa 3236"
          const lastFour = paymentToken?.paymentMethod?.displayName.split(' ')[1]
          const userName = {
            first_name: shippingContact?.givenName || storedUser?.first_name || '',
            last_name: shippingContact?.familyName || storedUser?.last_name || '',
          }

          const paymentMethod = {
            network: `${paymentToken?.paymentMethod?.network}`.toLowerCase(),
            name: `${userName.last_name.toUpperCase()}/${userName.first_name.toUpperCase()}`,
            pan_mask: `************${lastFour}`,
            input_mode: PaymentMethodInputTypes.FORTIS_APPLE_PAY_WEB,
            card_type: `${paymentToken?.paymentMethod?.type}`,
            card_token: JSON.stringify(paymentToken.paymentData),
          }
          localStorage.setItem('paymentMethods', JSON.stringify([paymentMethod, ...paymentMethods]))
          setPaymentMethod(paymentMethod)
          const paymentUser = {
            first_name: userName.first_name,
            last_name: userName.last_name,
            email: storedUser?.email || shippingContact?.emailAddress,
            phone: storedUser?.phone || shippingContact?.phoneNumber,
          }
          if (!storedUser?.phone) {
            setApplePayUser(paymentUser)
          }

          session.completePayment(result)
          resolve({ paymentMethod: paymentMethod, paymentUser: paymentUser })
        }

        session.oncancel = (event: any) => {
          reject(new Error('Apple Pay payment cancelled'))
        }

        // Start the Apple Pay session
        session.begin()
      } catch (error) {
        console.error('Failed to create ApplePaySession:', error)
        reject(error)
      }
    })
  }

  return {
    handleApplePayClick,
    isApplePaySupported,
  }
}
